import React, { useState, useMemo, useEffect, useRef } from "react";
import { ArrowLeft, X, ChevronDown, Users } from "lucide-react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Country, State } from "country-state-city";
import ISO6391 from "iso-639-1";
import { searchEthnicities, ALL_ETHNICITIES } from "../../../../constants/ethnicities";
import styles from "./Step4_1MemberMoreAboutYou.module.css";
import parentStyles from "../MemberOnboarding.module.css";
import { showToast } from "../../../../utils/toast";

interface Step4_1Props {
  onNext: (data?: any) => void;
  onBack: () => void;
  savedData?: any;
  canGoBack?: boolean;
}

const schema = yup.object({
  gender: yup.string().required("Gender is required"),
  country: yup.string().required("Country is required"),
  state: yup.string().required("State is required"),
  city: yup.string().required("City is required"),
  education: yup.string().required("Education level is required"),
  kids: yup.string().required("Number of kids is required"),
}).required();

type FormData = yup.InferType<typeof schema>;

export default function Step4_1MemberMoreAboutYou({
  onNext,
  onBack,
  savedData,
  canGoBack = true,
}: Step4_1Props) {
  const [languages, setLanguages] = useState<string[]>(() => {
    const initial = savedData?.language || savedData?.languages || ["English"];
    return Array.isArray(initial) ? initial.slice(0, 5) : ["English"];
  });
  const [langError, setLangError] = useState("");
  const [langSearch, setLangSearch] = useState("");
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  const [ethnicities, setEthnicities] = useState<string[]>(() => {
    const initial = savedData?.ethnicity || savedData?.ethnicities || [];
    if (Array.isArray(initial)) return initial.slice(0, 3);
    if (typeof initial === "string" && initial.trim()) {
      try {
        const parsed = JSON.parse(initial);
        if (Array.isArray(parsed)) return parsed.slice(0, 3);
      } catch {
        // Not JSON
      }
      return initial.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 3);
    }
    return [];
  });
  const [ethnicityError, setEthnicityError] = useState("");
  const [ethnicitySearch, setEthnicitySearch] = useState("");
  const [isEthnicityDropdownOpen, setIsEthnicityDropdownOpen] = useState(false);
  const ethnicityDropdownRef = useRef<HTMLDivElement>(null);

  const allLanguages = useMemo(() => {
    return [...ISO6391.getAllNames()].sort((a, b) => a.localeCompare(b));
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        langDropdownRef.current &&
        !langDropdownRef.current.contains(e.target as Node)
      ) {
        setIsLangDropdownOpen(false);
      }
      if (
        ethnicityDropdownRef.current &&
        !ethnicityDropdownRef.current.contains(e.target as Node)
      ) {
        setIsEthnicityDropdownOpen(false);
      }
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(e.target as Node)
      ) {
        setIsCountryDropdownOpen(false);
      }
      if (
        stateDropdownRef.current &&
        !stateDropdownRef.current.contains(e.target as Node)
      ) {
        setIsStateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredLanguages = useMemo(() => {
    const query = langSearch.trim().toLowerCase();
    if (!query) return allLanguages;
    return allLanguages.filter((l) => l.toLowerCase().includes(query));
  }, [allLanguages, langSearch]);

  const handleSelectLanguage = (selectedLang: string) => {
    if (languages.includes(selectedLang)) {
      return;
    }
    if (languages.length >= 5) {
      setLangError("You can select up to 5 languages");
      return;
    }
    const updated = [...languages, selectedLang];
    setLanguages(updated);
    setLangSearch("");
    setLangError("");
    if (updated.length >= 5) {
      setIsLangDropdownOpen(false);
    }
  };

  const handleLangInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const firstAvailable = filteredLanguages.find((l) => !languages.includes(l));
      if (firstAvailable) {
        handleSelectLanguage(firstAvailable);
      }
    } else if (e.key === "Escape") {
      setIsLangDropdownOpen(false);
    }
  };

  const filteredEthnicities = useMemo(() => {
    return searchEthnicities(ethnicitySearch);
  }, [ethnicitySearch]);

  const handleSelectEthnicity = (selectedEth: string) => {
    if (ethnicities.includes(selectedEth)) {
      return;
    }
    if (selectedEth === "Prefer not to say") {
      setEthnicities(["Prefer not to say"]);
      setEthnicitySearch("");
      setEthnicityError("");
      setIsEthnicityDropdownOpen(false);
      return;
    }

    const filtered = ethnicities.filter((e) => e !== "Prefer not to say");
    if (filtered.length >= 3) {
      setEthnicityError("You can select up to 3 ethnicities");
      return;
    }
    const updated = [...filtered, selectedEth];
    setEthnicities(updated);
    setEthnicitySearch("");
    setEthnicityError("");
    if (updated.length >= 3) {
      setIsEthnicityDropdownOpen(false);
    }
  };

  const handleEthnicityInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const firstAvailable = filteredEthnicities.find((eth) => !ethnicities.includes(eth));
      if (firstAvailable) {
        handleSelectEthnicity(firstAvailable);
      }
    } else if (e.key === "Escape") {
      setIsEthnicityDropdownOpen(false);
    }
  };

  const removeEthnicity = (eth: string) => {
    setEthnicities(ethnicities.filter((e) => e !== eth));
    setEthnicityError("");
  };

  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const initialCountryName = useMemo(() => {
    const raw = savedData?.country || "";
    if (!raw) return "";
    const match = Country.getAllCountries().find(
      c => c.isoCode.toLowerCase() === raw.toLowerCase() || c.name.toLowerCase() === raw.toLowerCase()
    );
    return match ? match.name : raw;
  }, [savedData?.country]);

  const initialValState = useMemo(() => {
    const rawState = savedData?.state || "";
    if (!rawState) return "";
    const matchCountry = Country.getAllCountries().find(
      c => c.isoCode.toLowerCase() === (savedData?.country || "").toLowerCase() || c.name.toLowerCase() === (savedData?.country || "").toLowerCase()
    );
    if (!matchCountry) return rawState;
    const states = State.getStatesOfCountry(matchCountry.isoCode);
    const matchState = states.find(
      s => s.isoCode.toLowerCase() === rawState.toLowerCase() || s.name.toLowerCase() === rawState.toLowerCase()
    );
    return matchState ? matchState.name : rawState;
  }, [savedData?.country, savedData?.state]);

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      gender: savedData?.gender || "",
      country: initialCountryName,
      state: initialValState,
      city: savedData?.city || "",
      education: savedData?.educationLevel || savedData?.education || "",
      kids: savedData?.numberOfKids !== undefined ? String(savedData.numberOfKids) : (savedData?.kids || ""),
    },
  });

  const selectedCountry = watch("country");
  const selectedState = watch("state");

  const selectedCountryObj = useMemo(() => {
    if (!selectedCountry) return null;
    return (
      allCountries.find(
        (c) =>
          c.name.toLowerCase() === selectedCountry.toLowerCase() ||
          c.isoCode.toLowerCase() === selectedCountry.toLowerCase()
      ) || null
    );
  }, [allCountries, selectedCountry]);

  const availableStates = useMemo(() => {
    if (!selectedCountryObj) return [];
    return State.getStatesOfCountry(selectedCountryObj.isoCode);
  }, [selectedCountryObj]);

  const prevCountryRef = useRef(selectedCountry);

  const [countrySearch, setCountrySearch] = useState(initialCountryName || "");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);

  const [stateSearch, setStateSearch] = useState(initialValState || "");
  const [isStateDropdownOpen, setIsStateDropdownOpen] = useState(false);
  const stateDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prevCountryRef.current !== selectedCountry) {
      prevCountryRef.current = selectedCountry;
      setValue("state", "");
      clearErrors("state");
      setStateSearch("");
    }
    setCountrySearch(selectedCountry || "");
  }, [selectedCountry, setValue, clearErrors]);

  useEffect(() => {
    setStateSearch(selectedState || "");
  }, [selectedState]);

  const filteredCountries = useMemo(() => {
    const q = countrySearch.trim().toLowerCase();
    if (!q || (selectedCountry && q === selectedCountry.toLowerCase())) {
      return allCountries;
    }
    return allCountries.filter((c) => c.name.toLowerCase().includes(q));
  }, [allCountries, countrySearch, selectedCountry]);

  const handleSelectCountry = (countryName: string) => {
    setValue("country", countryName, { shouldValidate: true });
    clearErrors("country");
    setCountrySearch(countryName);
    setIsCountryDropdownOpen(false);
  };

  const handleCountryInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredCountries.length > 0) {
        handleSelectCountry(filteredCountries[0].name);
      }
    } else if (e.key === "Escape") {
      setIsCountryDropdownOpen(false);
      setCountrySearch(selectedCountry || "");
    }
  };

  const filteredStates = useMemo(() => {
    const q = stateSearch.trim().toLowerCase();
    if (!q || (selectedState && q === selectedState.toLowerCase())) {
      return availableStates;
    }
    return availableStates.filter((s) => s.name.toLowerCase().includes(q));
  }, [availableStates, stateSearch, selectedState]);

  const handleSelectState = (stateName: string) => {
    setValue("state", stateName, { shouldValidate: true });
    clearErrors("state");
    setStateSearch(stateName);
    setIsStateDropdownOpen(false);
  };

  const handleStateInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredStates.length > 0) {
        handleSelectState(filteredStates[0].name);
      }
    } else if (e.key === "Escape") {
      setIsStateDropdownOpen(false);
      setStateSearch(selectedState || "");
    }
  };

  const onSubmit = (data: FormData) => {
    if (languages.length === 0) {
      setLangError("Please add at least one language");
      return;
    }
    if (languages.length > 5) {
      setLangError("You can select up to 5 languages");
      return;
    }
    if (ethnicities.length === 0) {
      setEthnicityError("Please select at least one ethnicity");
      return;
    }
    if (ethnicities.length > 3) {
      setEthnicityError("You can select up to 3 ethnicities");
      return;
    }
    setLangError("");
    setEthnicityError("");
    const payload = {
      gender: data.gender,
      numberOfKids: data.kids ? (data.kids === "3+" ? 3 : Number(data.kids) || data.kids) : 0,
      country: data.country,
      state: data.state,
      city: data.city.trim(),
      language: languages,
      languages: languages,
      ethnicity: Array.isArray(ethnicities) ? ethnicities : [ethnicities],
      ethnicities: Array.isArray(ethnicities) ? ethnicities : [ethnicities],
      educationLevel: data.education || "",
      education: data.education || "",
    };
    console.log("[Step4_1MemberMoreAboutYou] Form submitted -> calling onNext:", payload);
    onNext(payload);
  };

  const removeLanguage = (lang: string) => {
    setLanguages(languages.filter((l) => l !== lang));
    setLangError("");
  };

  return (
    <div className={styles.formCard}>
      <h1 className={styles.formTitle}>More About You</h1>
      <p className={styles.formSubtitle}>
        This is what your creators will see. You can update it at any time from your dashboard.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.formGrid}>

          <div className={styles.inputGroup}>
            <label className={styles.label}>Gender <span>*</span></label>
            <select
              className={styles.select}
              {...register("gender", {
                onChange: (e) => {
                  if (e.target.value) clearErrors("gender");
                },
              })}
              defaultValue=""
              required
            >
              <option value="" disabled>Select gender</option>
              <option value="female">Female</option>
              <option value="male">Male</option>
              <option value="non-binary">Non-binary</option>
            </select>
            {errors.gender && <p className={parentStyles.errorText}>{errors.gender.message}</p>}
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label}>Number of kids <span>*</span></label>
            <select
              className={styles.select}
              {...register("kids", {
                onChange: (e) => {
                  if (e.target.value) clearErrors("kids");
                },
              })}
              defaultValue=""
              required
            >
              <option value="" disabled>Select</option>
              <option value="0">0</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3+">3+</option>
            </select>
            {errors.kids && <p className={parentStyles.errorText}>{errors.kids.message}</p>}
          </div>

          <input type="hidden" {...register("country")} />
          <input type="hidden" {...register("state")} />

          <div className={styles.inputGroup}>
            <label className={styles.label}>Country <span>*</span></label>
            <div className={styles.inputWrapper} ref={countryDropdownRef}>
              <input
                type="text"
                placeholder="Select country"
                className={styles.inputSearchableSingle}
                value={countrySearch}
                onChange={(e) => {
                  setCountrySearch(e.target.value);
                  setIsCountryDropdownOpen(true);
                  if (errors.country) clearErrors("country");
                }}
                onFocus={(e) => {
                  setIsCountryDropdownOpen(true);
                  e.target.select();
                }}
                onKeyDown={handleCountryInputKeyDown}
              />
              <button
                type="button"
                className={styles.chevronButton}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsCountryDropdownOpen((prev) => !prev);
                }}
                tabIndex={-1}
                aria-label="Toggle country dropdown"
              >
                <ChevronDown
                  size={16}
                  className={`${styles.inputChevron} ${isCountryDropdownOpen ? styles.inputChevronOpen : ""
                    }`}
                />
              </button>

              {isCountryDropdownOpen && (
                <div className={styles.dropdownList}>
                  {filteredCountries.length > 0 ? (
                    filteredCountries.map((c) => {
                      const isSelected = selectedCountry === c.name;
                      return (
                        <div
                          key={c.isoCode}
                          className={`${styles.dropdownItem} ${isSelected ? styles.dropdownItemSelected : ""
                            }`}
                          onClick={() => handleSelectCountry(c.name)}
                        >
                          <span>{c.name}</span>
                          {isSelected && (
                            <span style={{ marginLeft: "auto", fontSize: 13, opacity: 0.8 }}>
                              ✓
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.dropdownEmpty}>No countries found</div>
                  )}
                </div>
              )}
            </div>
            {errors.country && <p className={parentStyles.errorText}>{errors.country.message}</p>}
          </div>

          <div className={styles.inputGroup}>
            <label className={styles.label}>State/Province <span>*</span></label>
            {availableStates.length > 0 ? (
              <div className={styles.inputWrapper} ref={stateDropdownRef}>
                <input
                  type="text"
                  placeholder="Select state/province"
                  className={styles.inputSearchableSingle}
                  value={stateSearch}
                  onChange={(e) => {
                    setStateSearch(e.target.value);
                    setIsStateDropdownOpen(true);
                    if (errors.state) clearErrors("state");
                  }}
                  onFocus={(e) => {
                    setIsStateDropdownOpen(true);
                    e.target.select();
                  }}
                  onKeyDown={handleStateInputKeyDown}
                />
                <button
                  type="button"
                  className={styles.chevronButton}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsStateDropdownOpen((prev) => !prev);
                  }}
                  tabIndex={-1}
                  aria-label="Toggle state dropdown"
                >
                  <ChevronDown
                    size={16}
                    className={`${styles.inputChevron} ${isStateDropdownOpen ? styles.inputChevronOpen : ""
                      }`}
                  />
                </button>

                {isStateDropdownOpen && (
                  <div className={styles.dropdownList}>
                    {filteredStates.length > 0 ? (
                      filteredStates.map((st) => {
                        const isSelected = selectedState === st.name;
                        return (
                          <div
                            key={`${st.countryCode}-${st.isoCode || st.name}`}
                            className={`${styles.dropdownItem} ${isSelected ? styles.dropdownItemSelected : ""
                              }`}
                            onClick={() => handleSelectState(st.name)}
                          >
                            <span>{st.name}</span>
                            {isSelected && (
                              <span style={{ marginLeft: "auto", fontSize: 13, opacity: 0.8 }}>
                                ✓
                              </span>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className={styles.dropdownEmpty}>No states found</div>
                    )}
                  </div>
                )}
              </div>
            ) : selectedCountryObj ? (
              <input
                type="text"
                placeholder="Enter state/province"
                className={styles.input}
                {...register("state", {
                  onChange: (e) => {
                    if (e.target.value) clearErrors("state");
                  },
                })}
              />
            ) : (
              <div className={styles.inputWrapper}>
                <input
                  type="text"
                  placeholder="Select country first"
                  className={styles.inputSearchableSingle}
                  disabled
                  value=""
                />
                <button
                  type="button"
                  className={styles.chevronButton}
                  disabled
                  tabIndex={-1}
                  style={{ opacity: 0.5, cursor: "not-allowed" }}
                >
                  <ChevronDown size={16} className={styles.inputChevron} />
                </button>
              </div>
            )}
            {errors.state && <p className={parentStyles.errorText}>{errors.state.message}</p>}
          </div>

          <div className={styles.inputGroupFull}>
            <label className={styles.label}>City <span>*</span></label>
            <input
              type="text"
              placeholder="City of residence"
              className={styles.input}
              {...register("city")}
            />
            {errors.city && <p className={parentStyles.errorText}>{errors.city.message}</p>}
          </div>

          <div className={`${styles.inputGroupFull} ${styles.languageInputGroup}`}>
            <label className={styles.label}>Language <span>*</span></label>
            <div className={styles.inputWrapper} ref={langDropdownRef}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={styles.inputIcon}
              >
                <g clipPath="url(#clip0_10493_163049)">
                  <path d="M7.64493 10.4711C7.61286 10.3105 6.5081 4.78668 6.47306 4.61164C6.41833 4.33777 6.17782 4.14062 5.89853 4.14062H4.72665C4.44735 4.14062 4.20685 4.33777 4.15212 4.61164C4.11653 4.78965 3.01064 10.3191 2.98024 10.471C2.91677 10.7884 3.12259 11.097 3.43989 11.1605C3.7572 11.2239 4.06591 11.0181 4.12935 10.7008L4.50388 8.82813H6.1213L6.49583 10.7009C6.55931 11.0183 6.86813 11.224 7.18528 11.1605C7.50259 11.097 7.70841 10.7884 7.64493 10.4711ZM4.73825 7.65625L5.207 5.3125H5.41817L5.88692 7.65625H4.73825Z" fill="#747474" />
                  <path d="M17.0312 8.82813H15.2734V8.24219C15.2734 7.91859 15.0111 7.65625 14.6875 7.65625C14.3639 7.65625 14.1016 7.91859 14.1016 8.24219V8.82813H12.3438C12.0202 8.82813 11.7578 9.09047 11.7578 9.41406C11.7578 9.73766 12.0202 10 12.3438 10H12.4855C12.8194 11.0786 13.3221 11.9065 13.8752 12.5699C13.425 12.9817 12.9695 13.3194 12.5637 13.644C12.311 13.8462 12.27 14.2149 12.4722 14.4676C12.6745 14.7204 13.0432 14.7611 13.2957 14.5591C13.7038 14.2326 14.1929 13.8698 14.6875 13.4159C15.1825 13.8702 15.6725 14.2336 16.0793 14.5591C16.332 14.7613 16.7007 14.7202 16.9028 14.4676C17.105 14.2149 17.064 13.8461 16.8113 13.644C16.4066 13.3202 15.9505 12.9821 15.4998 12.5699C16.0529 11.9065 16.5556 11.0786 16.8895 10H17.0312C17.3548 10 17.6172 9.73766 17.6172 9.41406C17.6172 9.09047 17.3548 8.82813 17.0312 8.82813ZM14.6875 11.7088C14.3135 11.2393 13.977 10.6788 13.7233 9.99609H15.6516C15.398 10.6788 15.0615 11.2393 14.6875 11.7088Z" fill="#747474" />
                  <path d="M18.2422 3.55469H9.78902L9.53785 1.53977C9.42813 0.661953 8.67828 0 7.79363 0H1.75781C0.788555 0 0 0.788555 0 1.75781V14.6875C0 15.6568 0.788555 16.4453 1.75781 16.4453H6.69855L6.94652 18.4602C7.05605 19.3363 7.8059 20 8.69078 20H18.2422C19.2114 20 20 19.2114 20 18.2422V5.3125C20 4.34324 19.2114 3.55469 18.2422 3.55469ZM1.75781 15.2734C1.43473 15.2734 1.17188 15.0106 1.17188 14.6875V1.75781C1.17188 1.43473 1.43473 1.17188 1.75781 1.17188H7.79363C8.08852 1.17188 8.33848 1.3925 8.375 1.68492C8.45992 2.36602 9.98469 14.5978 10.0689 15.2734H1.75781ZM8.07516 18.0371L7.87926 16.4453H9.45129L8.07516 18.0371ZM18.8281 18.2422C18.8281 18.5653 18.5653 18.8281 18.2422 18.8281H8.94039L11.171 16.2479C11.2852 16.1191 11.3364 15.9482 11.3128 15.7782L9.93512 4.72656H18.2422C18.5653 4.72656 18.8281 4.98941 18.8281 5.3125V18.2422Z" fill="#747474" />
                </g>
                <defs>
                  <clipPath id="clip0_10493_163049">
                    <rect width="20" height="20" fill="white" />
                  </clipPath>
                </defs>
              </svg>
              <input
                type="text"
                placeholder={
                  languages.length >= 5
                    ? "Maximum 5 languages selected"
                    : "You Speak in"
                }
                className={styles.inputWithIcon}
                value={langSearch}
                onChange={(e) => {
                  setLangSearch(e.target.value);
                  setIsLangDropdownOpen(true);
                  if (langError) setLangError("");
                }}
                onFocus={() => {
                  if (languages.length < 5) {
                    setIsLangDropdownOpen(true);
                  }
                }}
                onKeyDown={handleLangInputKeyDown}
                disabled={languages.length >= 5}
              />
              <button
                type="button"
                className={styles.chevronButton}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (languages.length < 5) {
                    setIsLangDropdownOpen((prev) => !prev);
                  }
                }}
                tabIndex={-1}
                aria-label="Toggle language dropdown"
              >
                <ChevronDown
                  size={16}
                  className={`${styles.inputChevron} ${isLangDropdownOpen ? styles.inputChevronOpen : ""
                    }`}
                />
              </button>

              {isLangDropdownOpen && languages.length < 5 && (
                <div className={styles.dropdownList}>
                  {filteredLanguages.length > 0 ? (
                    filteredLanguages.map((lang) => {
                      const isSelected = languages.includes(lang);
                      return (
                        <div
                          key={lang}
                          className={`${styles.dropdownItem} ${isSelected ? styles.dropdownItemSelected : ""
                            }`}
                          onClick={() => {
                            if (!isSelected) {
                              handleSelectLanguage(lang);
                            }
                          }}
                        >
                          <span>{lang}</span>
                          {isSelected && (
                            <span style={{ marginLeft: "auto", fontSize: 13, opacity: 0.8 }}>
                              ✓ Added
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.dropdownEmpty}>
                      No languages found
                    </div>
                  )}
                </div>
              )}
            </div>
            {langError && <p className={parentStyles.errorText}>{langError}</p>}

            {languages.length > 0 && (
              <div className={styles.tagsContainer}>
                {languages.map((lang) => (
                  <div key={lang} className={styles.tag}>
                    {lang}
                    <button type="button" onClick={() => removeLanguage(lang)} title={`Remove ${lang}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.inputGroupFull}>
            <label className={styles.label}>Ethnicity <span>*</span></label>
            <div className={styles.inputWrapper} ref={ethnicityDropdownRef}>
              <Users
                size={18}
                className={styles.inputIcon}
                style={{ color: "#747474" }}
              />
              <input
                type="text"
                placeholder={
                  ethnicities.length >= 3
                    ? "Maximum 3 ethnicities selected"
                    : "Select your ethnicity"
                }
                className={styles.inputWithIcon}
                value={ethnicitySearch}
                onChange={(e) => {
                  setEthnicitySearch(e.target.value);
                  setIsEthnicityDropdownOpen(true);
                  if (ethnicityError) setEthnicityError("");
                }}
                onFocus={() => {
                  if (ethnicities.length < 3) {
                    setIsEthnicityDropdownOpen(true);
                  }
                }}
                onKeyDown={handleEthnicityInputKeyDown}
                disabled={ethnicities.length >= 3}
              />
              <button
                type="button"
                className={styles.chevronButton}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (ethnicities.length < 3) {
                    setIsEthnicityDropdownOpen((prev) => !prev);
                  }
                }}
                tabIndex={-1}
                aria-label="Toggle ethnicity dropdown"
              >
                <ChevronDown
                  size={16}
                  className={`${styles.inputChevron} ${isEthnicityDropdownOpen ? styles.inputChevronOpen : ""
                    }`}
                />
              </button>

              {isEthnicityDropdownOpen && ethnicities.length < 3 && (
                <div className={styles.dropdownList}>
                  {filteredEthnicities.length > 0 ? (
                    filteredEthnicities.map((eth) => {
                      const isSelected = ethnicities.includes(eth);
                      return (
                        <div
                          key={eth}
                          className={`${styles.dropdownItem} ${isSelected ? styles.dropdownItemSelected : ""
                            }`}
                          onClick={() => {
                            if (!isSelected) {
                              handleSelectEthnicity(eth);
                            }
                          }}
                        >
                          <span>{eth}</span>
                          {isSelected && (
                            <span style={{ marginLeft: "auto", fontSize: 13, opacity: 0.8 }}>
                              ✓ Added
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className={styles.dropdownEmpty}>
                      No ethnicities found
                    </div>
                  )}
                </div>
              )}
            </div>
            {ethnicityError && <p className={parentStyles.errorText}>{ethnicityError}</p>}

            {ethnicities.length > 0 && (
              <div className={styles.tagsContainer}>
                {ethnicities.map((eth) => (
                  <div key={eth} className={styles.tag}>
                    {eth}
                    <button type="button" onClick={() => removeEthnicity(eth)} title={`Remove ${eth}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.inputGroupFull}>
            <label className={styles.label}>Education level <span>*</span></label>
            <select
              className={styles.select}
              {...register("education", {
                onChange: (e) => {
                  if (e.target.value) clearErrors("education");
                },
              })}
              defaultValue=""
              required
            >
              <option value="" disabled>Choose your qualification level</option>
              <option value="highschool">High School</option>
              <option value="bachelors">Bachelor's Degree</option>
              <option value="masters">Master's Degree</option>
              <option value="phd">PhD</option>
            </select>
            {errors.education && <p className={parentStyles.errorText}>{errors.education.message}</p>}
          </div>
        </div>

        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => {
              if (!canGoBack) {
                showToast("Cannot go backward", "info");
                return;
              }
              onBack();
            }}
            title={!canGoBack ? "Cannot go backward" : undefined}
            style={!canGoBack ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
          >
            <ArrowLeft size={18} /> Back
          </button>
          <button
            type="submit"
            className={styles.continueBtn}
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
